import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ProdutoService } from '../../../../../shared/services/apis/produto/produto.service';
import { CalcularPrecoAutomaticoRequest } from '../../../../../shared/services/requests/produto/calcular-preco-automatico.request';
import { MetodoAjustePrecoEnum } from '../../../../../shared/enums/produto/metodo-ajuste-preco.enum';
import { debounceTime } from 'rxjs/operators';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-produto-precos',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        NgxCurrencyDirective,
        SelectPesquisavelComponent,
        EmptyStateComponent
    ],
    templateUrl: './produto-precos.component.html',
    styleUrl: './produto-precos.component.css'
})
export class ProdutoPrecosComponent implements OnInit {

    @Input({ required: true }) form!: FormGroup;
    @Input() listaPreco: any[] = [];
    @Input() defaultCurrencyMask: any;
    @Input() dynamicCurrencyMask: any;
    @Input() percentageMask: any;
    @Input() set isKit(value: boolean) {
        this._isKit = value;
        this.reaplicarLockTodas();
    }
    get isKit(): boolean { return this._isKit; }
    private _isKit: boolean = false;
    @Input() idProduto: number | null = null;

    /** Para kit com isPrecoKitFixo=false, o preço da lista padrão vem da soma dos componentes */
    @Input() set totalKitComposicao(value: number) {
        this._totalKitComposicao = value;
        this.atualizarPrecoPadraoPorComposicao();
    }
    private _totalKitComposicao: number = 0;

    @Input() set dadosPrecosCarregados(precos: any[]) {
        if (precos && precos.length > 0) {
            precos.forEach(p => {
                const indexExistente = this.precos.controls.findIndex(
                    ctrl => ctrl.get('idListaPreco')?.value === p.idListaPreco
                );

                if (indexExistente !== -1) {
                    this.precos.at(indexExistente).patchValue(p);
                    if (this.isListaPadrao(p.idListaPreco)) {
                        this.precos.at(indexExistente).get('idListaPreco')?.disable();
                    }
                    if (this.isListaPadrao(p.idListaPreco)) {
                        this.precoPadraoIdentificado.emit(p.precoVenda);
                    }
                } else {
                    this.addPrecoRow(p);
                }

                // Aplicar regras de lock após carregar
                const idx = this.precos.controls.findIndex(
                    ctrl => ctrl.getRawValue()?.idListaPreco === p.idListaPreco
                );
                if (idx !== -1) {
                    this.aplicarLockPreco(idx);

                    // Se for automática e vier com preço zerado (ex: preloaded list do pai), calcular
                    if (this.isListaAutomatica(p.idListaPreco) && p.precoVenda === 0) {
                        this.calcularPrecoAutomatico(idx);
                    }
                }
            });

            // Após carregar todos os dados, subscrever ao preço da lista padrão
            this.subscreverPrecoPadrao();
        }
    }

    @Output() precoPadraoIdentificado = new EventEmitter<number>();

    /** Tracks which rows are currently loading auto-price */
    public calculandoPreco: Set<number> = new Set();
    private precoPadraoSubscribed = false;

    constructor(
        private fb: FormBuilder,
        private produtoService: ProdutoService
    ) { }

    ngOnInit(): void {
        this.form.get('isPrecoKitFixo')?.valueChanges.subscribe(() => {
            this.reaplicarLockTodas();
        });

        // Tentar subscrever caso a lista padrão já exista (criada pelo pai em checkDefaultPriceList)
        this.subscreverPrecoPadrao();
    }


    get precos(): FormArray {
        return this.form.get('precos') as FormArray;
    }

    get isPrecoKitFixo(): boolean {
        return this.form.get('isPrecoKitFixo')?.value ?? true;
    }

    getControl(item: any, controlName: string): FormControl {
        return item.get(controlName) as FormControl;
    }

    // ─── Helper Methods ────────────────────────────────────────────────────────

    addPrecoRow(data?: any): void {
        const disableSelect = this.isListaPadrao(data?.idListaPreco) || this.isListaSelecaoAutomatica(data?.idListaPreco);
        const group = this.fb.group({
            idProdutoPreco: [data?.idProdutoPreco],
            idListaPreco: [{ value: data?.idListaPreco, disabled: disableSelect }, Validators.required],
            precoVenda: [data?.precoVenda || 0],
            metodoAjuste: [{ value: data?.metodoAjuste || '', disabled: true }],
            inicioVigencia: [{ value: data?.inicioVigencia || null, disabled: true }],
            terminoVigencia: [{ value: data?.terminoVigencia || null, disabled: true }]
        });
        this.precos.push(group);

        const i = this.precos.length - 1;
        this.aplicarLockPreco(i);

        // Se adicionou a lista padrão via addPrecoRow, subscrever
        if (this.isListaPadrao(data?.idListaPreco)) {
            this.subscreverPrecoPadrao();
        }
    }

    /** Subscreve ao valueChanges do precoVenda da lista padrão para recalcular listas automáticas */
    private subscreverPrecoPadrao(): void {
        if (this.precoPadraoSubscribed) return;

        const padraoCtrl = this.precos.controls.find(ctrl => {
            const id = ctrl.getRawValue()?.idListaPreco;
            return this.isListaPadrao(id);
        });

        if (padraoCtrl) {
            this.precoPadraoSubscribed = true;
            padraoCtrl.get('precoVenda')?.valueChanges.pipe(
                debounceTime(500)
            ).subscribe(() => {
                this.recalcularListasAutomaticas();
            });
        }
    }

    removePrecoRow(index: number): void {
        this.precos.removeAt(index);
    }

    public isListaPadrao(id: number): boolean {
        const item = this.listaPreco?.find(l => l.id === id);
        return item?.listaPreco?.isPadrao || false;
    }

    public isListaAutomatica(id: number): boolean {
        const item = this.listaPreco?.find(l => l.id === id);
        if (!item) return false;
        return item.listaPreco?.idMetodoAjustePreco == MetodoAjustePrecoEnum.Automatico || item.listaPreco?.isSelecaoProdutosAutomatica;
    }

    /** Listas com seleção automática de produtos — sempre presentes, todos os campos travados */
    public isListaSelecaoAutomatica(id: number): boolean {
        const item = this.listaPreco?.find(l => l.id === id);
        return item?.listaPreco?.isSelecaoProdutosAutomatica || false;
    }

    /**
     * Determina se o campo de preço deve estar travado para edição.
     * Regras:
     * - Lista padrão + produto normal → ABERTO
     * - Lista padrão + kit (fixo ou não) → TRAVADO (reflete a composição ou o campo
     *   "Preço de venda no kit"; nunca é digitado diretamente aqui)
     * - Lista não-padrão automática → TRAVADO
     * - Lista não-padrão manual → ABERTO
     */
    public isPrecoTravado(i: number): boolean {
        const group = this.precos.at(i);
        const idLista = group.getRawValue()?.idListaPreco;
        const isPadrao = this.isListaPadrao(idLista);

        // Lista padrão: sempre travada quando é kit — o preço vem da composição ou do
        // campo dedicado "Preço de venda no kit", nunca digitado diretamente aqui.
        if (isPadrao) {
            return this.isKit;
        }

        // Demais listas: trava se for automático
        return this.isListaAutomatica(idLista);
    }

    /** Re-aplica as regras de lock em todas as linhas */
    private reaplicarLockTodas(): void {
        if (!this.precos) return;
        this.precos.controls.forEach((_, i) => this.aplicarLockPreco(i));
    }

    /** Recalcula o preço de todas as listas automáticas (não-padrão) */
    private recalcularListasAutomaticas(): void {
        this.precos.controls.forEach((_, i) => {
            const idLista = this.precos.at(i).getRawValue()?.idListaPreco;
            if (!this.isListaPadrao(idLista) && this.isListaAutomatica(idLista)) {
                this.calcularPrecoAutomatico(i);
            }
        });
    }

    /** Aplica enable/disable no control precoVenda conforme as regras */
    private aplicarLockPreco(i: number): void {
        const group = this.precos.at(i) as FormGroup;
        const ctrl = group.get('precoVenda');
        if (!ctrl) return;

        if (this.isPrecoTravado(i)) {
            ctrl.disable({ emitEvent: false });
        } else {
            ctrl.enable({ emitEvent: false });
        }
    }

    /** Chamado quando a lista de preços de uma linha muda */
    public onListaPrecoChange(i: number): void {
        this.aplicarLockPreco(i);

        const idLista = this.precos.at(i).getRawValue()?.idListaPreco;
        const eAutomatica = this.isListaAutomatica(idLista);
        console.log(eAutomatica)
        if (eAutomatica) {
            this.calcularPrecoAutomatico(i);
        }
    }

    /** Chama o backend para calcular o preço automático da linha i */
    private calcularPrecoAutomatico(i: number): void {
        const lista = this.getListaPrecoById(this.precos.at(i).getRawValue()?.idListaPreco);
        if (!lista?.listaPreco) return;

        const precoOriginal = this.getPrecoListaPadrao();
        if (precoOriginal == undefined || precoOriginal == null) return;

        const request: CalcularPrecoAutomaticoRequest = {
            idProduto: this.idProduto ?? undefined,
            precoOriginal,
            valorAjuste: lista.listaPreco.valorAjuste || 0,
            idAcaoAjustePreco: lista.listaPreco.idAcaoAjustePreco || 0,
            idUnidadeAjustePreco: lista.listaPreco.idUnidadeAjustePreco || 0,
            idTipoArredondamento: lista.listaPreco.idTipoArredondamento || 0,
        };

        this.calculandoPreco.add(i);
        this.produtoService.calcularPreco(request).subscribe({
            next: (res) => {
                this.calculandoPreco.delete(i);
                if (!res.isError && res.produtoListaPrecoStruct?.precoLista != null) {
                    this.precos.at(i).get('precoVenda')?.setValue(res.produtoListaPrecoStruct.precoLista, { emitEvent: false });
                }
            },
            error: () => this.calculandoPreco.delete(i)
        });
    }

    /** Retorna o preço da lista padrão (usada como base para cálculos automáticos) */
    private getPrecoListaPadrao(): number {
        const padraoCtrl = this.precos.controls.find(ctrl => {
            const id = ctrl.getRawValue()?.idListaPreco;
            return this.isListaPadrao(id);
        });
        return padraoCtrl?.getRawValue()?.precoVenda || 0;
    }

    /** Atualiza o preço da lista padrão com a soma dos componentes do kit */
    private atualizarPrecoPadraoPorComposicao(): void {
        if (!this.isKit || this.isPrecoKitFixo) return;

        const padraoCtrl = this.precos.controls.find(ctrl => {
            const id = ctrl.getRawValue()?.idListaPreco;
            return this.isListaPadrao(id);
        });

        if (padraoCtrl) {
            padraoCtrl.get('precoVenda')?.setValue(this._totalKitComposicao, { emitEvent: false });
            this.precoPadraoIdentificado.emit(this._totalKitComposicao);

            // Recalcular todas as listas automáticas com novo preço base
            this.precos.controls.forEach((_, i) => {
                const idLista = this.precos.at(i).getRawValue()?.idListaPreco;
                if (!this.isListaPadrao(idLista) && this.isListaAutomatica(idLista)) {
                    this.calcularPrecoAutomatico(i);
                }
            });
        }
    }

    public getListaPrecosDisponiveis(currentIndex: number): any[] {
        const idsAdicionados = this.precos.getRawValue()
            .map((p: any, idx: number) => idx === currentIndex ? null : p.idListaPreco)
            .filter((id: any) => id !== null);
        return this.listaPreco
            .filter(l => !idsAdicionados.includes(l.id))
            .map(l => ({
                ...l,
                disabled: l.listaPreco?.isSelecaoProdutosAutomatica || false
            }));
    }

    public getListaPrecoById(id: number): any {
        return this.listaPreco?.find(l => l.id === id);
    }

    public getDetalhesAjuste(id: number): string {
        const item = this.getListaPrecoById(id);
        if (!item?.listaPreco) return '';

        let symbol = '';
        const acao = item.acaoAjuste?.toLowerCase() || '';
        if (acao.includes('acréscimo') || acao.includes('aumento')) symbol = '+';
        else if (acao.includes('desconto') || acao.includes('redução')) symbol = '-';

        return `${symbol}${item.listaPreco.valorAjuste || 0}${item.unidadeAjuste || ''}`;
    }
}
